import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { number, string } from 'yup';
import { isInsuracePortalUser } from 'globalutils/fmUtils';
import { ROLES } from 'globalconfig/constants/keys';
import { checkServiceProvider } from './serviceProviderConfig';

export default function useSchema({ checkCameraFeature, renderComp = false }) {
  const { i18n } = useTranslation();
  const layout = window?.$environment?.CURRENT_LAYOUT;
  const SERVICE_PROVIDER = window.$environment?.SERVICE_PROVIDER;
  const insuracePortalUserRole = isInsuracePortalUser();

  const cameraSchema = useMemo(() => {
    return {
      ...(renderComp && {
        cameraType: string().when(['camera'], {
          is: (value) => value === 'Yes',
          then: string().required('common.fieldRequiredMessage'),
          otherwise: string(),
        }),
      }),

      cameraSerialNo: string().when('camera', {
        is: 'Yes',
        then: string()
          .trim()
          .required('common.fieldRequiredMessage')
          .matches(/^[a-zA-Z0-9\-]+$/, 'Only alphanumeric characters and hyphens are allowed')
          .min(1, 'Camera serial must be at least 1 character')
          .max(50, 'Camera serial must be at most 50 characters'),
        otherwise: string(),
      }),
      cameraPassword: string().when(['camera'], {
        is: (value) => value === 'Yes',
        then: string()
          .trim()
          .required('common.fieldRequiredMessage')
          .min(1, 'Password must be at least 1 character')
          .max(50, 'Password must be at most 50 characters'),
        otherwise: string(),
      }),
      plateNumber: string().required(i18n.t('common.fieldRequiredMessage')),
    };
  }, [i18n.language, renderComp]);

  const commonSchema = useMemo(() => {
    return {
      type: string().required(i18n.t('common.fieldRequiredMessage')),
      name: string().required(i18n.t('common.fieldRequiredMessage')).max(10, i18n.t('common.validName')),
      make: string().required(i18n.t('common.fieldRequiredMessage')),
      model: string().required(i18n.t('common.fieldRequiredMessage')),
      currentOdometer: number().typeError(i18n.t('common.validNumberMessage')).positive(i18n.t('common.positiveNumberMessage')),
      esn: string().when(['hardwareDeviceKey', 'deviceType'], {
        is: (value1, value2) =>
          (value2 === 'ELD' && value1?.value === 7001) ||
          (value2 === 'ELD' && value1?.value === 7007) ||
          (value2 === 'ELD' && value1?.value === 7010) ||
          value2 === 'Tracking',
        then: string().required('common.fieldRequiredMessage'),
        otherwise: string(),
      }),
      trackingDevice: string().when(['deviceType'], {
        is: (value) => value === 'Tracking',
        then: string().required('common.fieldRequiredMessage'),
        otherwise: string(),
      }),
      nextMaintenanceMileage: number()
        .typeError(i18n.t('common.validNumberMessage'))
        .positive(i18n.t('common.positiveNumberMessage')),
    };
  }, [i18n.language]);

  const regx = renderComp ? /[^\WqioqIOQ]+$/ : /[^\WIOQioq]+$/;

  const defaultSchema = useMemo(() => {
    return {
      vin: string()
        .required(i18n.t('common.fieldRequiredMessage'))
        .matches(regx, i18n.t('common.isValidVin'))
        .test('len', i18n.t('common.isValidVin'), (val) => val?.length === 17),
      company: insuracePortalUserRole ? string().required('common.fieldRequiredMessage') : string(),
      ...(checkCameraFeature() && cameraSchema),
      ...checkServiceProvider('schema_grade', SERVICE_PROVIDER, false, { renderComp }),
      ...commonSchema,
    };
  }, [i18n.language]);

  const tenantSchema = useMemo(() => {
    return {
      [ROLES.WEBFLEET]: {
        esn: string().when(['hardwareDeviceKey', 'deviceType'], {
          is: (value1, value2) =>
            (value2 === 'ELD' && value1?.value === 7001) ||
            (value2 === 'ELD' && value1?.value === 7007) ||
            (value2 === 'ELD' && value1?.value === 7010) ||
            value2 === 'Tracking',
          then: string().required('common.fieldRequiredMessage'),
          otherwise: string(),
        }),
        trackingDevice: string().when(['deviceType'], {
          is: (value) => value === 'Tracking',
          then: string().required('common.fieldRequiredMessage'),
          otherwise: string(),
        }),
      },
      default: defaultSchema,
    };
  }, [i18n.language]);

  const schema = useMemo(() => {
    if (layout !== 'Clutch') {
      return {
        ...checkServiceProvider('schema_layout_clutch', SERVICE_PROVIDER, false),
        next_maintenance_date: string().required(i18n.t('common.fieldRequiredMessage')),
        ...(checkCameraFeature() && cameraSchema),
        ...commonSchema,
      };
    }

    return tenantSchema[SERVICE_PROVIDER] || tenantSchema['default'];
  }, [i18n.language]);

  return { schema };
}
